/**
 * In-memory flag definitions for OpenFeature's LocalProvider.
 * This file contains the flag definitions for the LocalProvider.
 * It is used to define the flags that are available in the LocalProvider.
 * The flags are defined as an object with the flag key as the key and the flag definition as the value.
 * The flag definition is an object with the following properties:
 * - type: The type of the flag. This can be one of 'boolean', 'string', 'number', or 'object'.
 * - defaultValue: The default value of the flag. This is the value that will be returned if the flag is not found in the LocalProvider.
 * - description: A description of the flag. This is used to provide information about the flag to the user.
 * - values: An array of possible values for the flag. This is used to provide a list of possible values for the flag to the user.
 */

interface EvenSplitDefinition<T> {
  weightedSplit?: false
  values: T[]
}

interface WeightedSplitDefinition<T> {
  weightedSplit: true
  values: {
    value: T
    weight: number
  }[]
}

export type FeatureDefinition<T> = {
  type: 'boolean' | 'string' | 'number'
  defaultValue: T
  description: string
} & (EvenSplitDefinition<T> | WeightedSplitDefinition<T>)

export type FeaturePrimitive = boolean | string | number

export interface FeaturesDefinition {
  [key: string]: FeatureDefinition<FeaturePrimitive>
}

/**
 * INTERNAL SOP DOCS FOR SETTING UP A FLAG AND SPLIT TEST: https://redbrickmedia.atlassian.net/wiki/spaces/Duplex/pages/3686957075/ConfigCat+Testing+SOP
 */
export const flags: FeaturesDefinition = {
  splittestname: {
    type: 'string',
    defaultValue: 'control',
    description:
      'The name of the A/B test split to use. It is the parent flag that child flag/configs will look to for evaluation.',
    values: ['control', 'DXT-####_variant_a'],
  },
  layoutSkeleton: {
    type: 'string',
    defaultValue: 'control',
    description:
      'Which layout skeleton to serve. Assignment lives in ConfigCat targeting, not here: LocalProvider picks evenly across `values`, so listing a skeleton would hand it to every tenant in local dev. Override locally with ?layoutSkeleton=<name>.',
    values: ['control', '{splittestname}'],
  },
  siteSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of site settings to use',
    values: ['control', '{splittestname}'],
  },
  layoutSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of layout settings to use',
    values: ['control', '{splittestname}'],
  },
  adSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of layout settings to use',
    values: ['control', '{splittestname}'],
  },
  summaryPageSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of summary page settings to use',
    values: ['control', '{splittestname}'],
  },
  contentSource: {
    type: 'string',
    defaultValue: 'content-api-v1',
    description: 'Which content source to use',
    values: [
      'content-api-v1',
      'content-api-recommended',
      'discover-feed',
      'dplxapi',
      'quartz-feed',
    ],
  },
  showCategoryInsteadOfSource: {
    type: 'boolean',
    defaultValue: false,
    description: 'DXT-1403: show category instead of source on homepage',
    values: [true, false],
  },
  discoverTab: {
    type: 'boolean',
    defaultValue: false,
    description: 'DXT-1453: Add Discover tab as the default homepage tab.',
    values: [true, false],
  },
  tabBadges: {
    type: 'string',
    defaultValue: '',
    description:
      'JSON configuration for tab badges. Format: [{"tab":"TabName","variant":"pill|count","label":"Text","tone":"blue|red|dark-blue"}]',
    values: [''],
  },
  nativeAdProvider: {
    defaultValue: 'outbrain',
    description: 'Which native ad provider to use',
    type: 'string',
    values: ['outbrain', 'taboola', 'dplxapi'],
  },
  nativeFallbackEnabled: {
    type: 'boolean',
    defaultValue: true,
    description:
      'SUP-877: Master switch for the native ad fallback. When true, an empty AdX slot renders the native provider (Outbrain/Taboola) fallback via the slotRenderEnded listener; when false, no fallback listener is registered. Scope per tenant via ConfigCat targeting.',
    values: [true, false],
  },
  enableMixedArticleFeed: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Enabling/disabling mixed article feed. Enabling it would allow fetching multiple article types from content api',
    values: [true, false],
  },
  enableSportsFeed: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Gives Sports a share of the quartz feed. Discover renders from it, so target it by cohort only.',
    values: [false],
  },
  localNewsEnabled: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Enables local news experiment. When true, users see news from local publishers based on their detected city. Currently only supports Houston.',
    values: [true, false],
  },
  engagedUserAdSlot: {
    type: 'boolean',
    defaultValue: true,
    description:
      'Show a 970x250 ad slot instead of sponsored tiles for engaged users',
    values: [true, false],
  },
  enableLegoLand: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Whether to enable lego-land features. This is the main switch for all lego-land related features and should be used for testing and gradual rollout.',
    values: [true, false],
  },
  legoLandLayoutConfig: {
    type: 'string',
    defaultValue: 'home3-col-layout',
    description:
      'Which layout config to use in lego-land. Valid values are the keys of LAYOUT_CONFIGS in configs/registry/layoutConfigs.ts',
    values: ['home3-col-layout', 'home1-col-layout', 'home-bento-layout'],
  },
  contentOrder: {
    type: 'string',
    defaultValue: 'inherit',
    description:
      'DXT-2168: How the home feed reorders when a news tile is clicked. inherit = no change, shuffleOnClick = full shuffle, moveOneOnClick = first item rotates to the end.',
    values: ['inherit', 'shuffleOnClick', 'moveOneOnClick'],
  },
  shiftNewsSearchBoxVariant: {
    type: 'string',
    defaultValue: 'control',
    description:
      'SUP-434: ShiftNews.com "Opera-style" search box icon variant for AppEsteem compliance. control = no Yahoo icon (existing magnifying glass on the left); variation-a = greyscale Yahoo! icon on the left, magnifying glass on the right (Variation 1 / Opera style); variation-b = magnifying glass on the left, greyscale Yahoo! icon on the right (Variation 2 / swapped). Scope to ShiftNews via ConfigCat targeting. Replaces the SUP-377 placeholder-text test (same values reused).',
    values: ['control', 'variation-a', 'variation-b'],
  },
  absolutePublishedDateAfterHours: {
    type: 'number',
    defaultValue: 0,
    description:
      'SUP-252: Show the absolute published date (e.g. "June 15, 2026") instead of relative time (e.g. "2h") for article cards older than this many hours. 0 = always relative (disabled). Set per partner via ConfigCat targeting rules (e.g. 12, 24).',
    values: [0, 12, 24],
  },
  searchBoxVariant: {
    type: 'string',
    defaultValue: 'off',
    description:
      'SUP-51/SUP-52/SUP-583: Search box experiment variant (tenant-agnostic, scope via ConfigCat targeting). "off" = control, "scrim" = blur backdrop on input focus (Variation A / SUP-52), "gradient" = animated gradient border (Variation B / SUP-51), "top" = top-of-page pill search box (SUP-583).',
    values: ['off', 'scrim', 'gradient', 'top'],
  },
  articleStreamEnabled: {
    type: 'boolean',
    defaultValue: false,
    description:
      'SUP-460: When true, the article page renders the endless infinite-scroll article stream layout instead of the classic single-article layout. Scope per tenant via ConfigCat targeting.',
    values: [true, false],
  },
  shiftNewsHome: {
    type: 'boolean',
    defaultValue: false,
    description:
      'SUP-611: ShiftNews homepage layout test. Bundles three CSS changes: grey (#e5e7eb) leaderboard ad band with its 16px vertical margin removed, no category tab bar above the content, and extra spacing (32px top / 64px bottom) around the search box. Scope to ShiftNews via ConfigCat targeting.',
    values: [true, false],
  },
  enableUserIdentity: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Master switch for the shared user-identity integration: capture the first-party userId (GAM PPID), resolve the HEM via the Retention.com provider, and inject it into Prebid via window.pbjs. Off by default; enable per tenant via ConfigCat targeting for split testing.',
    values: [true, false],
  },
  randomizeFirstLoadOrder: {
    type: 'boolean',
    defaultValue: false,
    description:
      'SUP-504: When true, randomizes the order of articles in the feed on first load so position bias can be measured independently of content quality. Control keeps the default publish-date + cluster-size sort. Scope the test bucket via ConfigCat targeting.',
    values: [true, false],
  },
}
